<template>
  <StatCard
    title="How long tasks take"
    :subtitle="subtitle"
    :loading="loading"
    :empty="isEmpty"
    empty-title="Nothing completed in this period"
    empty-hint="Finish a task and its turnaround time appears here."
  >
    <div ref="chartEl" class="w-full h-64"></div>
  </StatCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import StatCard from './StatCard.vue'

const props = defineProps({
  data: { type: Array, default: () => [] },
  medianHours: { type: Number, default: null },
  loading: { type: Boolean, default: false },
})

const chartEl = ref(null)
let chart = null

const isEmpty = computed(() => !props.loading && props.data.every(d => d.count === 0))

const subtitle = computed(() => {
  if (props.medianHours === null || props.medianHours === undefined) return 'From created to completed'
  const median = props.medianHours < 48
    ? `${props.medianHours.toFixed(1)} hours`
    : `${(props.medianHours / 24).toFixed(1)} days`
  return `From created to completed. Median ${median}.`
})

function render() {
  if (!chartEl.value || isEmpty.value) return
  // Highcharts arrives from a CDN via layouts/_highcharts.html.erb. If that is
  // blocked or still in flight, degrade to an empty chart area rather than
  // throwing and taking the rest of the page down with it.
  if (typeof Highcharts === 'undefined') return
  if (chart) { chart.destroy(); chart = null }

  chart = Highcharts.chart(chartEl.value, {
    chart: { type: 'column', height: 256, spacing: [16, 16, 8, 8] },
    xAxis: { categories: props.data.map(d => d.bucket), lineWidth: 0, tickLength: 0 },
    yAxis: { min: 0, allowDecimals: false, title: { text: null } },
    legend: { enabled: false },
    tooltip: { pointFormat: '<b>{point.y}</b> tasks' },
    plotOptions: { column: { borderWidth: 0, borderRadius: 3, pointPadding: 0.08 } },
    series: [{ name: 'Tasks', data: props.data.map(d => d.count), color: '#6366f1' }],
  })
}

watch(() => props.data, () => nextTick(render), { deep: true })
onMounted(() => { if (!isEmpty.value) nextTick(render) })
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
